import React, { useState, useEffect } from 'react';
import { 
  Plus, Edit3, Trash2, Save, ChevronDown, ChevronRight, 
  FolderOpen, Users, Box, Clock, X, RotateCcw
} from 'lucide-react';
import { useAppStore } from '../stores/appStore';
import { Button, Badge, Card, Modal } from '../components/ui';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Projects() {
  const {
    categories, customers, seatTypes, projects, lines,
    fetchCategories, fetchCustomers, fetchSeatTypes, fetchProjects,
    createCategory, createCustomer, createSeatType, createProject,
    fetchLines,
  } = useAppStore();

  const [expandedCat, setExpandedCat] = useState(null);
  const [expandedCust, setExpandedCust] = useState(null);
  const [expandedProj, setExpandedProj] = useState(null);
  const [operations, setOperations] = useState([]);

  // Category CRUD
  const [showCatForm, setShowCatForm] = useState(false);
  const [catName, setCatName] = useState('');
  const [catCode, setCatCode] = useState('');
  const [editCatId, setEditCatId] = useState(null);

  // Customer CRUD
  const [showCustForm, setShowCustForm] = useState(false);
  const [custName, setCustName] = useState('');
  const [custCode, setCustCode] = useState('');
  const [custCatId, setCustCatId] = useState('');
  const [editCustId, setEditCustId] = useState(null);

  // Seat Type CRUD
  const [showSTForm, setShowSTForm] = useState(false);
  const [stName, setStName] = useState('');
  const [stCode, setStCode] = useState('');
  const [stCustId, setStCustId] = useState('');
  const [stCatId, setStCatId] = useState('');
  const [editSTId, setEditSTId] = useState(null);

  // Project CRUD
  const [showProjForm, setShowProjForm] = useState(false);
  const [projName, setProjName] = useState('');
  const [projSeatTypeId, setProjSeatTypeId] = useState('');
  const [projLineId, setProjLineId] = useState('');
  const [projQty, setProjQty] = useState(100);
  const [editProjId, setEditProjId] = useState(null);

  // Operation edit
  const [editOpId, setEditOpId] = useState(null);
  const [editOpName, setEditOpName] = useState('');
  const [editOpDur, setEditOpDur] = useState(0);
  const [showAddOp, setShowAddOp] = useState(false);
  const [newOpName, setNewOpName] = useState('');
  const [newOpDur, setNewOpDur] = useState(0);
  const [newOpStationId, setNewOpStationId] = useState('');
  const [projStations, setProjStations] = useState([]);

  useEffect(() => {
    fetchCategories(); fetchCustomers(); fetchSeatTypes();
    fetchProjects(); fetchLines();
  }, []);

  const loadProjectOps = async (projectId) => {
    try {
      const res = await axios.get(`${API_URL}/api/operations?project_id=${projectId}`);
      setOperations(res.data);
      // Load stations for the project's line
      const proj = projects.find(p => p.id === projectId);
      if (proj?.line_id) {
        const stRes = await axios.get(`${API_URL}/api/stations?line_id=${proj.line_id}`);
        setProjStations(stRes.data);
      }
    } catch (err) { console.error(err); }
  };

  const toggleProject = (projId) => {
    if (expandedProj === projId) { setExpandedProj(null); setOperations([]); }
    else { setExpandedProj(projId); loadProjectOps(projId); }
  };

  // Category handlers
  const handleSaveCat = async () => {
    if (!catName) return;
    if (editCatId) {
      await axios.put(`${API_URL}/api/categories/${editCatId}`, { name: catName, code: catCode });
      setEditCatId(null);
    } else {
      await createCategory({ name: catName, code: catCode || catName.substring(0, 3).toUpperCase() });
    }
    setCatName(''); setCatCode(''); setShowCatForm(false);
    fetchCategories();
  };

  const handleDeleteCat = async (id) => {
    await axios.delete(`${API_URL}/api/categories/${id}`);
    fetchCategories();
  };

  const startEditCat = (cat) => {
    setEditCatId(cat.id); setCatName(cat.name); setCatCode(cat.code); setShowCatForm(true);
  };

  // Customer handlers
  const handleSaveCust = async () => {
    if (!custName || !custCatId) return;
    const catObj = categories.find(c => c.id === custCatId);
    if (editCustId) {
      await axios.put(`${API_URL}/api/customers/${editCustId}`, {
        name: custName, code: custCode, category_id: custCatId, category_name: catObj?.name
      });
      setEditCustId(null);
    } else {
      await createCustomer({
        name: custName, code: custCode || custName.substring(0, 4).toUpperCase(),
        category_id: custCatId, category_name: catObj?.name
      });
    }
    setCustName(''); setCustCode(''); setCustCatId(''); setShowCustForm(false);
    fetchCustomers();
  };

  const handleDeleteCust = async (id) => {
    if (!window.confirm('Delete this customer and all related seat types, projects, and operations?')) return;
    try {
      await axios.delete(`${API_URL}/api/customers/${id}`);
      fetchCustomers(); fetchSeatTypes(); fetchProjects();
    } catch (err) { console.error(err); }
  };

  const startEditCust = (cust) => {
    setEditCustId(cust.id); setCustName(cust.name); setCustCode(cust.code); setCustCatId(cust.category_id); setShowCustForm(true);
  };

  // Seat Type handlers
  const handleDeleteST = async (id) => {
    if (!window.confirm('Delete this seat type and all related projects and operations?')) return;
    try {
      await axios.delete(`${API_URL}/api/seat-types/${id}`);
      fetchSeatTypes(); fetchProjects();
    } catch (err) { console.error(err); }
  };
  const handleSaveST = async () => {
    if (!stName || !stCustId) return;
    const custObj = customers.find(c => c.id === stCustId);
    if (editSTId) {
      await axios.put(`${API_URL}/api/seat-types/${editSTId}`, {
        name: stName, code: stCode, customer_id: stCustId, category_id: stCatId || custObj?.category_id,
      });
      setEditSTId(null);
    } else {
      await createSeatType({
        name: stName, code: stCode || stName.substring(0, 4).toUpperCase(),
        customer_id: stCustId, category_id: stCatId || custObj?.category_id,
      });
    }
    setStName(''); setStCode(''); setStCustId(''); setStCatId(''); setShowSTForm(false);
    fetchSeatTypes();
  };

  const startEditST = (st) => {
    setEditSTId(st.id); setStName(st.name); setStCode(st.code);
    setStCustId(st.customer_id); setStCatId(st.category_id || ''); setShowSTForm(true);
  };

  // Project handlers
  const handleSaveProj = async () => {
    if (!projName || !projSeatTypeId || !projLineId) return;
    if (editProjId) {
      await axios.put(`${API_URL}/api/projects/${editProjId}`, {
        name: projName, seat_type_id: projSeatTypeId,
        line_id: projLineId, target_quantity: projQty, status: 'active',
      });
      setEditProjId(null);
    } else {
      await createProject({
        name: projName, seat_type_id: projSeatTypeId,
        line_id: projLineId, target_quantity: projQty, status: 'active',
      });
    }
    setProjName(''); setProjSeatTypeId(''); setProjLineId(''); setProjQty(100); setShowProjForm(false);
    fetchProjects();
  };

  const startEditProj = (proj) => {
    setEditProjId(proj.id); setProjName(proj.name); setProjSeatTypeId(proj.seat_type_id);
    setProjLineId(proj.line_id); setProjQty(proj.target_quantity || 0); setShowProjForm(true);
  };

  const handleDeleteProj = async (id) => {
    await axios.delete(`${API_URL}/api/projects/${id}`);
    fetchProjects();
  };

  // Operation handlers (inline)
  const handleEditOp = (op) => { setEditOpId(op.id); setEditOpName(op.name); setEditOpDur(op.duration_sec); };
  const handleSaveOp = async () => {
    await axios.put(`${API_URL}/api/operations/${editOpId}/quick-update`, { name: editOpName, duration_sec: editOpDur });
    setEditOpId(null);
    if (expandedProj) loadProjectOps(expandedProj);
  };
  const handleDeleteOp = async (opId) => {
    await axios.delete(`${API_URL}/api/operations/${opId}/quick-delete`);
    if (expandedProj) loadProjectOps(expandedProj);
  };
  const handleAddOp = async () => {
    if (!newOpName || newOpDur <= 0 || !newOpStationId || !expandedProj) return;
    await axios.post(`${API_URL}/api/operations/quick-create`, {
      name: newOpName, duration_sec: newOpDur, project_id: expandedProj, station_id: newOpStationId,
    });
    setNewOpName(''); setNewOpDur(0); setNewOpStationId(''); setShowAddOp(false);
    loadProjectOps(expandedProj);
  };

  const getCustomersForCat = (catId) => customers.filter(c => c.category_id === catId);
  const getSeatTypesForCust = (custId) => seatTypes.filter(st => st.customer_id === custId);
  const getProjectsForST = (stId) => projects.filter(p => p.seat_type_id === stId);

  // Group operations by station
  const groupedOps = {};
  operations.forEach(op => {
    const key = op.station_id || 'unassigned';
    if (!groupedOps[key]) groupedOps[key] = [];
    groupedOps[key].push(op);
  });

  return (
    <div className="space-y-6" data-testid="projects-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-heading font-bold">Projects & Operations</h1>
        <div className="flex gap-2">
          <Button size="sm" variant="secondary" onClick={() => setShowCatForm(true)} data-testid="add-category-btn">
            <Plus className="w-3 h-3" /> Category
          </Button>
          <Button size="sm" variant="secondary" onClick={() => setShowCustForm(true)} data-testid="add-customer-btn">
            <Plus className="w-3 h-3" /> Customer
          </Button>
          <Button size="sm" variant="secondary" onClick={() => setShowSTForm(true)} data-testid="add-seat-type-btn">
            <Plus className="w-3 h-3" /> Seat Type
          </Button>
          <Button size="sm" variant="primary" onClick={() => setShowProjForm(true)} data-testid="add-project-btn">
            <Plus className="w-3 h-3" /> Project
          </Button>
        </div>
      </div>

      {/* Hierarchical Tree View */}
      <div className="space-y-2">
        {categories.map(cat => (
          <Card key={cat.id} className="overflow-hidden">
            {/* Category Row */}
            <div 
              className="flex items-center justify-between p-3 bg-slate-50 cursor-pointer hover:bg-slate-100"
              onClick={() => setExpandedCat(expandedCat === cat.id ? null : cat.id)}
              data-testid={`cat-row-${cat.code}`}
            >
              <div className="flex items-center gap-2">
                {expandedCat === cat.id ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                <FolderOpen className="w-4 h-4 text-amber-500" />
                <span className="font-semibold">{cat.name}</span>
                <Badge variant="secondary">{cat.code}</Badge>
                <span className="text-xs text-slate-500">{getCustomersForCat(cat.id).length} customers</span>
              </div>
              <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                <button className="p-1 hover:bg-blue-50 rounded text-blue-500" onClick={() => startEditCat(cat)}><Edit3 className="w-3.5 h-3.5" /></button>
                <button className="p-1 hover:bg-red-50 rounded text-red-500" onClick={() => handleDeleteCat(cat.id)}><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>

            {/* Customers under this Category */}
            {expandedCat === cat.id && (
              <div className="border-t">
                {getCustomersForCat(cat.id).map(cust => (
                  <div key={cust.id}>
                    <div 
                      className="flex items-center justify-between p-3 pl-8 bg-white cursor-pointer hover:bg-slate-50 border-b"
                      onClick={() => setExpandedCust(expandedCust === cust.id ? null : cust.id)}
                      data-testid={`cust-row-${cust.code}`}
                    >
                      <div className="flex items-center gap-2">
                        {expandedCust === cust.id ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                        <Users className="w-4 h-4 text-blue-500" />
                        <span className="font-medium">{cust.name}</span>
                        <Badge variant="secondary">{cust.code}</Badge>
                        <span className="text-xs text-slate-400">
                          {getSeatTypesForCust(cust.id).length} seat types, {getSeatTypesForCust(cust.id).reduce((sum, st) => sum + getProjectsForST(st.id).length, 0)} projects
                        </span>
                      </div>
                      <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                        <button className="p-1 hover:bg-blue-50 rounded text-blue-500" onClick={() => startEditCust(cust)} data-testid={`edit-cust-${cust.code}`}><Edit3 className="w-3.5 h-3.5" /></button>
                        <button className="p-1 hover:bg-red-50 rounded text-red-500" onClick={() => handleDeleteCust(cust.id)}><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </div>

                    {/* Seat Types and Projects */}
                    {expandedCust === cust.id && getSeatTypesForCust(cust.id).map(st => (
                      <div key={st.id}>
                        <div className="flex items-center justify-between p-2 pl-14 bg-blue-50 border-b">
                          <div className="flex items-center gap-2">
                            <Box className="w-3 h-3 text-purple-500" />
                            <span className="text-sm font-medium text-purple-700">{st.name}</span>
                            <Badge variant="accent">{st.code}</Badge>
                          </div>
                          <div className="flex gap-1">
                            <button className="p-1 hover:bg-blue-50 rounded text-blue-500" onClick={() => startEditST(st)} data-testid={`edit-st-${st.id}`}><Edit3 className="w-3.5 h-3.5" /></button>
                            <button className="p-1 hover:bg-red-50 rounded text-red-500" onClick={() => handleDeleteST(st.id)} data-testid={`delete-st-${st.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                          </div>
                        </div>

                        {/* Projects for this Seat Type */}
                        {getProjectsForST(st.id).map(proj => (
                          <div key={proj.id} className="border-b">
                            <div
                              className="flex items-center justify-between p-2 pl-20 bg-white cursor-pointer hover:bg-emerald-50"
                              onClick={() => toggleProject(proj.id)}
                              data-testid={`proj-row-${proj.id}`}
                            >
                              <div className="flex items-center gap-2">
                                {expandedProj === proj.id ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                                <span className="text-sm font-semibold text-emerald-700">{proj.name}</span>
                                {proj.line_code && <Badge variant="secondary">{proj.line_code}</Badge>}
                                <span className="text-xs text-slate-400">Qty: {proj.target_quantity}</span>
                              </div>
                              <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                                <button className="p-1 hover:bg-blue-50 rounded text-blue-500" onClick={() => startEditProj(proj)} data-testid={`edit-proj-${proj.id}`}><Edit3 className="w-3.5 h-3.5" /></button>
                                <button className="p-1 hover:bg-red-50 rounded text-red-500" onClick={() => handleDeleteProj(proj.id)}><Trash2 className="w-3.5 h-3.5" /></button>
                              </div>
                            </div>

                            {/* Operations for this Project */}
                            {expandedProj === proj.id && (
                              <div className="bg-slate-50 p-4 pl-24 border-t">
                                <div className="flex items-center justify-between mb-3">
                                  <span className="text-sm font-medium text-slate-700">
                                    Operations ({operations.length}) - 
                                    Total: {Math.floor(operations.reduce((s, o) => s + o.duration_sec, 0) / 60)}m {operations.reduce((s, o) => s + o.duration_sec, 0) % 60}s
                                  </span>
                                  <Button size="sm" variant="secondary" onClick={() => { setShowAddOp(true); setNewOpStationId(projStations[0]?.id || ''); }}
                                    data-testid="proj-add-op-btn">
                                    <Plus className="w-3 h-3" /> Add Operation
                                  </Button>
                                </div>

                                {showAddOp && (
                                  <div className="mb-3 p-3 border-2 border-blue-400 rounded-lg bg-white space-y-2">
                                    <input type="text" placeholder="Operation name" value={newOpName} onChange={e => setNewOpName(e.target.value)}
                                      className="w-full border rounded px-3 py-2 text-sm" data-testid="proj-new-op-name" />
                                    <div className="flex gap-2">
                                      <input type="number" placeholder="Duration (sec)" value={newOpDur || ''} onChange={e => setNewOpDur(parseInt(e.target.value) || 0)}
                                        className="w-32 border rounded px-3 py-2 text-sm" data-testid="proj-new-op-dur" />
                                      <select value={newOpStationId} onChange={e => setNewOpStationId(e.target.value)} className="flex-1 border rounded px-3 py-2 text-sm">
                                        {projStations.map(s => <option key={s.id} value={s.id}>Station {s.station_number}</option>)}
                                      </select>
                                      <Button size="sm" variant="success" onClick={handleAddOp}><Save className="w-3 h-3" /> Save</Button>
                                      <Button size="sm" variant="secondary" onClick={() => setShowAddOp(false)}>Cancel</Button>
                                    </div>
                                  </div>
                                )}

                                {/* Operations grouped by station */}
                                {Object.entries(groupedOps).sort(([a], [b]) => {
                                  const stA = projStations.find(s => s.id === a);
                                  const stB = projStations.find(s => s.id === b);
                                  return (stA?.station_number || 99) - (stB?.station_number || 99);
                                }).map(([stationId, stOps]) => {
                                  const stInfo = projStations.find(s => s.id === stationId);
                                  const stTotal = stOps.reduce((s, o) => s + o.duration_sec, 0);
                                  return (
                                    <div key={stationId} className="mb-3">
                                      <div className="flex items-center gap-2 mb-1">
                                        <span className="text-xs font-bold text-slate-600 bg-slate-200 px-2 py-0.5 rounded">
                                          Station {stInfo?.station_number || '?'}
                                        </span>
                                        <span className="text-xs text-slate-500">
                                          {stOps.length} ops - {Math.floor(stTotal / 60)}m {stTotal % 60}s
                                        </span>
                                      </div>
                                      <div className="space-y-1">
                                        {stOps.sort((a, b) => (a.sequence_order || 0) - (b.sequence_order || 0)).map((op, idx) => (
                                          <div key={op.id} className="flex items-center gap-2 p-2 bg-white rounded border-l-4 border-blue-400">
                                            {editOpId === op.id ? (
                                              <>
                                                <span className="text-xs bg-slate-200 px-1.5 py-0.5 rounded">{idx + 1}</span>
                                                <input type="text" value={editOpName} onChange={e => setEditOpName(e.target.value)}
                                                  className="flex-1 border rounded px-2 py-1 text-sm" />
                                                <input type="number" value={editOpDur} onChange={e => setEditOpDur(parseInt(e.target.value) || 0)}
                                                  className="w-20 border rounded px-2 py-1 text-sm text-right" />
                                                <span className="text-xs text-slate-500">s</span>
                                                <button onClick={handleSaveOp} className="p-1 text-emerald-600 hover:bg-emerald-50 rounded"><Save className="w-4 h-4" /></button>
                                                <button onClick={() => setEditOpId(null)} className="p-1 text-slate-400 hover:bg-slate-50 rounded"><RotateCcw className="w-4 h-4" /></button>
                                              </>
                                            ) : (
                                              <>
                                                <span className="text-xs bg-slate-200 px-1.5 py-0.5 rounded">{idx + 1}</span>
                                                <span className="flex-1 text-sm">{op.name}</span>
                                                <span className="text-sm font-mono text-slate-600">{op.duration_sec}s</span>
                                                <button onClick={() => handleEditOp(op)} className="p-1 text-blue-500 hover:bg-blue-50 rounded"><Edit3 className="w-3.5 h-3.5" /></button>
                                                <button onClick={() => handleDeleteOp(op.id)} className="p-1 text-red-500 hover:bg-red-50 rounded"><Trash2 className="w-3.5 h-3.5" /></button>
                                              </>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  );
                                })}
                                {operations.length === 0 && <p className="text-sm text-slate-500">No operations yet. Click 'Add Operation' to create one.</p>}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                ))}
                {getCustomersForCat(cat.id).length === 0 && (
                  <p className="p-3 pl-8 text-sm text-slate-500">No customers in this category</p>
                )}
              </div>
            )}
          </Card>
        ))}
        {categories.length === 0 && <p className="text-slate-500">No categories yet. Create one to get started.</p>}
      </div>

      {/* Add Category Modal */}
      <Modal isOpen={showCatForm} onClose={() => { setShowCatForm(false); setEditCatId(null); setCatName(''); setCatCode(''); }}
        title={editCatId ? 'Edit Category' : 'Add Category'}>
        <div className="space-y-3">
          <input type="text" placeholder="Category name (e.g. Coach, Train)" value={catName} onChange={e => setCatName(e.target.value)}
            className="w-full border rounded px-3 py-2" data-testid="cat-name-input" />
          <input type="text" placeholder="Code (e.g. COACH)" value={catCode} onChange={e => setCatCode(e.target.value)}
            className="w-full border rounded px-3 py-2" data-testid="cat-code-input" />
          <Button variant="primary" onClick={handleSaveCat} data-testid="cat-save-btn"><Save className="w-4 h-4" /> Save</Button>
        </div>
      </Modal>

      {/* Add Customer Modal */}
      <Modal isOpen={showCustForm} onClose={() => { setShowCustForm(false); setEditCustId(null); }}
        title={editCustId ? 'Edit Customer' : 'Add Customer'}>
        <div className="space-y-3">
          <select value={custCatId} onChange={e => setCustCatId(e.target.value)} className="w-full border rounded px-3 py-2" data-testid="cust-cat-select">
            <option value="">Select Category</option>
            {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <input type="text" placeholder="Customer name (e.g. BART, MTA)" value={custName} onChange={e => setCustName(e.target.value)}
            className="w-full border rounded px-3 py-2" data-testid="cust-name-input" />
          <input type="text" placeholder="Code" value={custCode} onChange={e => setCustCode(e.target.value)}
            className="w-full border rounded px-3 py-2" data-testid="cust-code-input" />
          <Button variant="primary" onClick={handleSaveCust} data-testid="cust-save-btn"><Save className="w-4 h-4" /> Save</Button>
        </div>
      </Modal>

      {/* Add Seat Type Modal */}
      <Modal isOpen={showSTForm} onClose={() => { setShowSTForm(false); setEditSTId(null); }} title={editSTId ? "Edit Seat Type" : "Add Seat Type"}>
        <div className="space-y-3">
          <select value={stCatId} onChange={e => { setStCatId(e.target.value); setStCustId(''); }} className="w-full border rounded px-3 py-2">
            <option value="">Select Category</option>
            {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select value={stCustId} onChange={e => setStCustId(e.target.value)} className="w-full border rounded px-3 py-2">
            <option value="">Select Customer</option>
            {(stCatId ? customers.filter(c => c.category_id === stCatId) : customers).map(c =>
              <option key={c.id} value={c.id}>{c.name}</option>
            )}
          </select>
          <input type="text" placeholder="Seat Type name (e.g. ESOS, METRO)" value={stName} onChange={e => setStName(e.target.value)}
            className="w-full border rounded px-3 py-2" />
          <input type="text" placeholder="Code" value={stCode} onChange={e => setStCode(e.target.value)}
            className="w-full border rounded px-3 py-2" />
          <Button variant="primary" onClick={handleSaveST}><Save className="w-4 h-4" /> Save</Button>
        </div>
      </Modal>

      {/* Add Project Modal */}
      <Modal isOpen={showProjForm} onClose={() => { setShowProjForm(false); setEditProjId(null); }} title={editProjId ? "Edit Project" : "Add Project"}>
        <div className="space-y-3">
          <input type="text" placeholder="Project name" value={projName} onChange={e => setProjName(e.target.value)}
            className="w-full border rounded px-3 py-2" data-testid="proj-name-input" />
          <select value={projSeatTypeId} onChange={e => setProjSeatTypeId(e.target.value)} className="w-full border rounded px-3 py-2">
            <option value="">Select Seat Type</option>
            {seatTypes.map(st => <option key={st.id} value={st.id}>{st.name} ({st.code})</option>)}
          </select>
          <select value={projLineId} onChange={e => setProjLineId(e.target.value)} className="w-full border rounded px-3 py-2">
            <option value="">Select Line</option>
            {lines.map(l => <option key={l.id} value={l.id}>{l.code} - {l.name}</option>)}
          </select>
          <input type="number" placeholder="Target quantity" value={projQty} onChange={e => setProjQty(parseInt(e.target.value) || 0)}
            className="w-full border rounded px-3 py-2" />
          <Button variant="primary" onClick={handleSaveProj} data-testid="proj-save-btn"><Save className="w-4 h-4" /> Save</Button>
        </div>
      </Modal>
    </div>
  );
}
